<script setup lang="ts">
import BaseButton from '@/components/ui/BaseButton.vue';
import AiBadgePill from './AiBadgePill.vue';
import type { components } from '@/api/generated/schema';

type SuggestedActionDto = components['schemas']['SuggestedActionDto'];

interface Props {
  actions: SuggestedActionDto[];
}
defineProps<Props>();

const emit = defineEmits<{ comment: [label: string] }>();
</script>

<template>
  <div
    v-if="actions.length > 0"
    class="bg-surface border border-border rounded-lg p-4"
    data-testid="ai-suggested-actions"
  >
    <!-- Teal AI header (CLAUDE.md: AI signal = teal only) -->
    <div class="flex items-center gap-2 mb-3">
      <svg class="w-4 h-4 text-teal-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      <span class="text-caption font-semibold text-teal-700 uppercase tracking-wide">AI Suggested Actions</span>
      <AiBadgePill label="AI" />
    </div>

    <ol class="flex flex-col gap-3">
      <li
        v-for="(action, i) in actions"
        :key="action.id"
        class="flex items-start gap-3"
      >
        <span class="flex-shrink-0 w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-caption font-mono tabular-nums flex items-center justify-center">
          {{ i + 1 }}
        </span>
        <div class="flex-1 min-w-0">
          <p class="text-body text-fg font-medium">{{ action.label }}</p>
          <p class="text-caption text-muted mt-0.5">{{ action.rationale }}</p>
        </div>
        <!-- Only "Draft reply" action is stub-enabled via comment; others are stub-disabled -->
        <BaseButton
          v-if="action.label.toLowerCase().includes('comment') || action.label.toLowerCase().includes('note')"
          variant="ai"
          size="sm"
          @click="emit('comment', action.label)"
        >
          Draft
        </BaseButton>
        <BaseButton
          v-else
          variant="secondary"
          size="sm"
          disabled
          title="Coming in a future slice"
        >
          Action
        </BaseButton>
      </li>
    </ol>
  </div>
</template>
